import { renderKoDataTable, renderKoEmptyState } from '~/components/templates/listPageDataTable';
import { renderRecruitingStatusCell, renderRecruitingTextCell } from '~/components/templates/recruiting/RecruitingTable';
import { html, LpButton, LpTable } from '~/design-system';
function renderActionButton(binding, label = 'Edit', icon = 'pencil-simple') {
    return LpButton({
        ariaLabel: label,
        prefixIcon: icon,
        size: 'sm',
        shape: 'square',
        variant: 'ghost',
        className: 'list-edit-action',
        attributes: { 'data-bind': binding },
    });
}
export function renderPartnerQuestionsTable() {
    const columns = [
        { key: 'order', label: 'Order', width: 88, minWidth: 72, resizable: true },
        { key: 'question', label: 'Question', minWidth: 360, resizable: true },
        { key: 'required', label: 'Required', width: 128, minWidth: 104, resizable: true },
        { key: 'actions', label: 'Actions', align: 'center', width: 128, minWidth: 112, required: true },
    ];
    return renderKoDataTable({
        title: 'Questions',
        titleIcon: 'question',
        caption: 'Partner questions',
        columns,
        className: 'partner-edit-questions-table',
        children: html `
            ${LpTable.Body({
            attributes: { 'data-bind': 'foreach: questions' },
            children: LpTable.Row({
                attributes: {
                    draggable: 'true',
                    'data-bind': 'event: { dragstart: function () { $parent.__draggedQuestion = $data; }, dragover: function () { return false; }, drop: function () { $parent.onQuestionDragDrop($parent.__draggedQuestion, $data); } }',
                },
                children: html `
                        ${renderRecruitingTextCell({ column: columns[0], binding: '$data.displayOrder || $data.order || $index() + 1' })}
                        ${renderRecruitingTextCell({ column: columns[1], binding: '$parent.getQuestionLabel($data)', clampLines: 4, titleBinding: '$parent.getQuestionLabel($data)' })}
                        ${renderRecruitingStatusCell({
                    column: columns[2],
                    textBinding: '$parent.isQuestionRequired($data) ? "Required" : "Optional"',
                    cssBinding: `
                                'task-status--success': $parent.isQuestionRequired($data),
                                'task-status--pending': !$parent.isQuestionRequired($data)
                            `,
                })}
                        ${LpTable.Cell({
                    column: columns[3],
                    children: html `
                                <div class="list-page-row-actions">
                                    ${renderActionButton('click: $parent.onClickEditQuestion', 'Edit question')}
                                    ${renderActionButton('click: $parent.onClickRemoveQuestion', 'Remove question', 'trash')}
                                </div>
                            `,
                })}
                    `,
            }),
        })}
            ${renderKoEmptyState({ colSpan: columns.length, visibleBinding: 'questions().length === 0', title: 'No questions assigned', description: 'Add questions to this partner.', icon: 'question' })}
        `,
    });
}
export function renderPartnerContractsTable() {
    const columns = [
        { key: 'id', label: 'ID', width: 88, minWidth: 72, resizable: true },
        { key: 'name', label: 'Contract', minWidth: 240, resizable: true },
        { key: 'status', label: 'Status', width: 128, minWidth: 104, resizable: true },
        { key: 'actions', label: 'Actions', align: 'center', width: 96, minWidth: 88, required: true },
    ];
    return renderKoDataTable({
        title: 'Contracts',
        titleIcon: 'file-text',
        caption: 'Partner contracts',
        columns,
        className: 'partner-edit-contracts-table',
        children: html `
            ${LpTable.Body({
            attributes: { 'data-bind': 'foreach: contracts' },
            children: LpTable.Row({
                children: html `
                        ${renderRecruitingTextCell({ column: columns[0], binding: 'id' })}
                        ${renderRecruitingTextCell({ column: columns[1], binding: '$data.name || $data.title || $data.label || ""', titleBinding: '$data.name || $data.title || $data.label || ""' })}
                        ${renderRecruitingStatusCell({
                    column: columns[2],
                    textBinding: '$data.statusLabel || (Number($data.active || $data.status || 0) === 1 ? "Active" : "Inactive")',
                    cssBinding: `
                                'task-status--success': Number($data.active || $data.status || 0) === 1,
                                'task-status--pending': Number($data.active || $data.status || 0) !== 1
                            `,
                })}
                        ${LpTable.Cell({ column: columns[3], children: renderActionButton('click: $parent.onClickEditContract') })}
                    `,
            }),
        })}
            ${renderKoEmptyState({ colSpan: columns.length, visibleBinding: 'contracts().length === 0', title: 'No contracts found', description: 'No active contracts are attached to this partner.', icon: 'file-text' })}
        `,
    });
}
export function renderPartnerTransmissionsTable() {
    const columns = [
        { key: 'id', label: 'ID', width: 88, minWidth: 72, resizable: true },
        { key: 'name', label: 'Transmission', minWidth: 240, resizable: true },
        { key: 'type', label: 'Type', width: 180, minWidth: 140, resizable: true },
        { key: 'live', label: 'Live', width: 128, minWidth: 104, resizable: true },
        { key: 'actions', label: 'Actions', align: 'center', width: 96, minWidth: 88, required: true },
    ];
    return renderKoDataTable({
        title: 'Transmissions',
        titleIcon: 'paper-plane-tilt',
        caption: 'Partner transmissions',
        columns,
        className: 'partner-edit-transmissions-table',
        children: html `
            ${LpTable.Body({
            attributes: { 'data-bind': 'foreach: transmissions' },
            children: LpTable.Row({
                children: html `
                        ${renderRecruitingTextCell({ column: columns[0], binding: 'id' })}
                        ${renderRecruitingTextCell({ column: columns[1], binding: '$data.name || $data.label || ""', titleBinding: '$data.name || $data.label || ""' })}
                        ${renderRecruitingTextCell({ column: columns[2], binding: '$data.transmissionTypeName || $data.typeName || $data.transmissionType || ""' })}
                        ${renderRecruitingStatusCell({
                    column: columns[3],
                    textBinding: '$data.liveLabel || (Number($data.live || 0) === 1 ? "Live" : "Not Live")',
                    cssBinding: `
                                'task-status--success': Number($data.live || 0) === 1,
                                'task-status--pending': Number($data.live || 0) !== 1
                            `,
                })}
                        ${LpTable.Cell({ column: columns[4], children: renderActionButton('click: $parent.onClickEditTransmission') })}
                    `,
            }),
        })}
            ${renderKoEmptyState({ colSpan: columns.length, visibleBinding: 'transmissions().length === 0', title: 'No transmissions found', description: 'No transmissions are attached to this partner.', icon: 'paper-plane-tilt' })}
        `,
    });
}
export function renderPartnerCreativesTable() {
    const columns = [
        { key: 'id', label: 'ID', width: 88, minWidth: 72, resizable: true },
        { key: 'title', label: 'Creative', width: 220, minWidth: 180, resizable: true },
        { key: 'summary', label: 'Summary', width: 280, minWidth: 220, resizable: true },
        { key: 'status', label: 'Status', width: 128, minWidth: 104, resizable: true },
        { key: 'actions', label: 'Actions', align: 'center', width: 96, minWidth: 88, required: true },
    ];
    return renderKoDataTable({
        title: 'Creatives',
        titleIcon: 'image',
        caption: 'Partner creatives',
        columns,
        className: 'partner-edit-creatives-table',
        children: html `
            ${LpTable.Body({
            attributes: { 'data-bind': 'foreach: creatives' },
            children: LpTable.Row({
                children: html `
                        ${renderRecruitingTextCell({ column: columns[0], binding: 'id' })}
                        ${renderRecruitingTextCell({ column: columns[1], binding: '$data.title || $data.partnerTitle || $data.name || ""', titleBinding: '$data.title || $data.partnerTitle || $data.name || ""' })}
                        ${renderRecruitingTextCell({ column: columns[2], binding: '$data.descShortLabel || $data.descShort || $data.shortDescription || ""', clampLines: 3, titleBinding: '$data.descShortLabel || $data.descShort || $data.shortDescription || ""' })}
                        ${renderRecruitingStatusCell({
                    column: columns[3],
                    textBinding: '$data.statusLabel || (Number($data.status || $data.creativeStatus || 0) === 1 ? "Active" : "Inactive")',
                    cssBinding: `
                                'task-status--success': Number($data.status || $data.creativeStatus || 0) === 1,
                                'task-status--pending': Number($data.status || $data.creativeStatus || 0) !== 1
                            `,
                })}
                        ${LpTable.Cell({ column: columns[4], children: renderActionButton('click: $parent.onClickEditCreative') })}
                    `,
            }),
        })}
            ${renderKoEmptyState({ colSpan: columns.length, visibleBinding: 'creatives().length === 0', title: 'No creatives found', description: 'No creatives are attached to this partner.', icon: 'image' })}
        `,
    });
}
